class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = null;
    // When inside the search overlay, we don't auto-close on empty input
    // — the overlay's own empty state stays visible.
    this.isInOverlay = this.hasAttribute('data-overlay-inner');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 250).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    if (!this.isInOverlay) {
      // Inside the overlay, focusout-to-close is wrong — the overlay owns close.
      this.addEventListener('focusout', this.onFocusOut.bind(this));
    }
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      if (this.isInOverlay) {
        // Leave the empty state visible; just clear any previous results.
        this.clearResults();
      } else {
        this.close(true);
      }
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      if (!this.isInOverlay) this.close(true);
      return;
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    // Group-aware: walk across all result items regardless of which <ul> they're in.
    const allElements = Array.from(this.querySelectorAll('[role="option"]'));
    if (!allElements.length) return;
    let activeElement = allElements[0];

    if (moveUp && !selectedElement) return;

    if (this.statusElement) this.statusElement.textContent = '';

    if (selectedElement) {
      const currentIndex = allElements.indexOf(selectedElement);
      let nextIndex;
      if (moveUp) {
        nextIndex = currentIndex <= 0 ? allElements.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= allElements.length - 1 ? 0 : currentIndex + 1;
      }
      activeElement = allElements[nextIndex];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Cancel any in-flight fetch — prevents stale results overwriting fresh ones.
    if (this.abortController) {
      try { this.abortController.abort(); } catch (_) {}
    }
    this.abortController = new AbortController();

    const resourceTypes = 'product,collection,query,article,page';
    // Shopify caps resources[limit] at 10 per resource type. Push it to the max
    // and widen searched fields to include body (description) + tag + sku, so queries
    // like "chrome" match more than just products with "chrome" literally in the title.
    const searchFields = 'title,product_type,variants.title,vendor,tag,body,variants.sku';
    const params = [
      'q=' + encodeURIComponent(searchTerm),
      encodeURIComponent('resources[type]') + '=' + encodeURIComponent(resourceTypes),
      encodeURIComponent('resources[limit]') + '=10',
      encodeURIComponent('resources[options][unavailable_products]') + '=last',
      encodeURIComponent('resources[options][fields]') + '=' + encodeURIComponent(searchFields),
      'section_id=predictive-search'
    ].join('&');

    fetch(`${routes.predictive_search_url}?${params}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          if (!this.isInOverlay) this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) return;
        const resultsMarkup = section.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        if (!this.isInOverlay) this.close();
        // Swallow so the console stays clean when the overlay is open.
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  clearResults() {
    this.predictiveSearchResults.innerHTML = '';
    this.removeAttribute('results');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', false);
    this.isOpen = false;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveEl) this.setLiveRegionText(liveEl.textContent);
  }

  getResultsMaxHeight() {
    // Not used inside the overlay (overlay owns its own scroll).
    if (this.isInOverlay) return null;
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.isInOverlay) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    if (!this.isInOverlay) {
      this.predictiveSearchResults.removeAttribute('style');
    }

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
